'use client';

import {
  CategoryProductsContentFragment,
  SearchResultFragment,
  SortValues,
} from '@business/gql/graphql';
import { Button, Radio } from '@ngg/components';
import { Dictionary } from '@ts/dictionary';
import { SortingOptions } from '@ts/sort';
import FacetContentSearch from '@ui/components/Facets/FacetContentSearch';
import RoundBadge from '@ui/components/RoundBadge/RoundBadge';
import SortBy from '../SortBy/SortBy';
import CloseButton from '@ui/components/button/buttonClose';
import Icon from '@ui/components/icon/icon';
import useSort from '@utils/hooks/sort/useSort';
import useWindowSize from '@utils/useWindowSize';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import MobileDrawer from '../MobileDrawer';
import { useMenuStore } from '@stores/menu.store';
import useShallowSearchParams from '@utils/hooks/sort/useShallowSearchParams';
import getNumberOfActiveFilters from '@utils/hooks/sort/helpers/getNumberOfActiveFilters';
import { useMounted } from '@utils/hooks/useMounted';
import Paragraph from '@ui/components/Paragraph/Paragraph';

type Props = {
  title: string;
  dictionary: Dictionary;
  productsCount?: string;
  facets:
    | CategoryProductsContentFragment['facets']
    | SearchResultFragment['facets'];
  currentSort: SortValues;
};

function ListTopBar({
  title,
  dictionary,
  facets,
  productsCount,
  currentSort,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [sortingDrawerOpen, setSortingDrawerOpen] = useState<boolean>(false);
  const { isBreakpoint } = useWindowSize('md');
  const updateSort = useSort();
  const { setDrawerIsOpen } = useMenuStore();
  const { shallowSearchParams, resetShallowSearchParams } =
    useShallowSearchParams();
  const mounted = useMounted();

  const sortingOptions = useMemo<SortingOptions>(() => {
    const entries = Object.entries(dictionary)
      .filter(([key]) => key.startsWith('sort_'))
      // If the key is 'sort_relevance' it should always be first
      .sort(([a], [b]) => {
        if (a === 'sort_relevance') return -1;
        if (b === 'sort_relevance') return 1;
        return 0;
      });

    return entries.map(([key, label]) => ({
      id: key.replace('sort_', ''),
      label,
    })) as SortingOptions;
  }, [dictionary]);

  const numberOfActiveFilters = getNumberOfActiveFilters(shallowSearchParams);

  const closeFilterDrawer = useCallback(() => {
    setFilterDrawerOpen(false);
  }, []);

  const closeSortingDrawer = useCallback(() => {
    setSortingDrawerOpen(false);
  }, []);

  const clearSearchParams = () => {
    const newSearchParams = new URLSearchParams(window.location.search);
    ['category', 'designer', 'brand', 'price', 'sort'].forEach((key) => {
      newSearchParams.delete(key);
    });
    router.push(pathname + '?' + newSearchParams.toString());
  };

  useEffect(() => {
    setDrawerIsOpen(filterDrawerOpen);
  }, [filterDrawerOpen, setDrawerIsOpen]);

  useEffect(() => {
    setDrawerIsOpen(sortingDrawerOpen);
  }, [sortingDrawerOpen, setDrawerIsOpen]);

  return (
    <>
      <div className="flex items-center justify-between font-sans text-xxs font-bold max-sm:text-xxs">
        <Paragraph className="flex-1 items-center whitespace-nowrap uppercase">
          {title}
        </Paragraph>
        <Button
          onClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
          className="flex items-center justify-center gap-1 whitespace-nowrap uppercase focus:outline-none md:hidden">
          <span>{dictionary.filter}</span>
          {numberOfActiveFilters > 0 && mounted && (
            <RoundBadge>{numberOfActiveFilters}</RoundBadge>
          )}
          <Icon name="filter" color="black" size={16} />
        </Button>
        <div className="flex-1">
          <SortBy
            isMobile={!isBreakpoint}
            options={sortingOptions}
            value={currentSort}
            onChange={updateSort}
            openSortingDrawer={() =>
              !isBreakpoint && setSortingDrawerOpen(true)
            }
          />
        </div>
      </div>

      {facets && !isBreakpoint && (
        <MobileDrawer
          CloseButton={() => (
            <div className="flex items-center">
              <button
                className="text-xxs font-normal underline focus:outline-none"
                onClick={() => {
                  resetShallowSearchParams();
                  clearSearchParams();
                }}>
                {dictionary?.resetAll}
              </button>
              {CloseButton(closeFilterDrawer, '-mr-2')}
            </div>
          )}
          onClose={closeFilterDrawer}
          open={filterDrawerOpen}
          title={
            <div className="flex items-center justify-center gap-1 whitespace-nowrap text-xxs font-bold uppercase">
              <span>{dictionary?.filter}</span>
              <Icon name="filter" color="black" size={16} />
            </div>
          }>
          <FacetContentSearch
            isMobile={!isBreakpoint}
            dictionary={dictionary}
            facets={facets}
          />
          <div className="p-2">
            <Button
              className="btn-primary disabled:border-gray-150 disabled:bg-grey-150 disabled:text-grey-300 h-10 grow bg-black text-base text-white disabled:opacity-100"
              fullWidth
              onClick={() => {
                closeFilterDrawer();
              }}>
              {dictionary?.showProducts}
              {productsCount && <span>({productsCount})</span>}
            </Button>
          </div>
        </MobileDrawer>
      )}

      {!isBreakpoint && (
        <MobileDrawer
          title={<span className="text-sm uppercase">{dictionary.sort}</span>}
          open={sortingDrawerOpen}
          CloseButton={() => CloseButton(closeSortingDrawer, '-mr-2')}
          onClose={closeSortingDrawer}>
          <div className="space-y-1 p-4">
            {sortingOptions.map((option) => (
              <div
                key={option.id}
                className="flex"
                onClick={() => updateSort(option.id)}>
                <Radio
                  id={`sort-${option.id}`}
                  checked={option.id === currentSort}>
                  {option.label}
                </Radio>
              </div>
            ))}
          </div>
        </MobileDrawer>
      )}
    </>
  );
}

export default ListTopBar;
